import { Play, Check, MessageCircle, ShieldCheck, Sparkles, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SignatureFlowMock } from "@/components/landing/product-mocks";

type Props = {
  pt: boolean;
  ctaHref: string;
  ctaLabel: string;
  signup: boolean;
  /** Admin-editable copy: copy(key, fallback) returns the override from website settings or the fallback. */
  copy: (key: string, fallback: string) => string;
};

/** Sales-first opening (the ZapSign/DocuSign pattern): promise, proof, one primary action. The film sits further down. */
export function SalesHero({ pt, ctaHref, ctaLabel, signup, copy }: Props) {
  const badges: Array<[typeof Check, string]> = pt
    ? [[ShieldCheck, "Validade jurídica: Lei 14.063 e MP 2.200-2"], [MessageCircle, "Assina pelo WhatsApp, sem app e sem cadastro"], [Sparkles, "A IA posiciona as assinaturas"], [Building2, "Nativo no Dynamics 365"]]
    : [[ShieldCheck, "Legally valid in Brazil (Law 14.063, MP 2.200-2)"], [MessageCircle, "Signs over WhatsApp, no app, no sign-up"], [Sparkles, "AI places the signatures"], [Building2, "Native to Dynamics 365"]];
  const scrollToFilm = () => document.querySelector('[data-cinematic="film"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <section className="mx-auto grid max-w-6xl items-center gap-12 px-5 pb-16 pt-14 sm:px-8 lg:grid-cols-[1.05fr_1fr] lg:pb-24 lg:pt-20" data-hero="sales">
      <div>
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-[var(--fs-moss)]">{copy("hero.eyebrow", pt ? "Assinatura eletrônica · WhatsApp · Dynamics 365" : "E-signature · WhatsApp · Dynamics 365")}</p>
        <h1 className="mt-4 text-4xl font-semibold leading-[1.05] tracking-[-0.03em] text-[var(--fs-forest)] sm:text-5xl lg:text-[3.75rem]">
          {copy("hero.title", pt ? "Contratos assinados em minutos, pelo WhatsApp." : "Contracts signed in minutes, over WhatsApp.")}
        </h1>
        <p className="mt-6 max-w-xl text-lg leading-relaxed text-gray-600">
          {copy("hero.subtitle", pt
            ? "Você envia, a IA posiciona as assinaturas e o cliente assina no celular sem baixar app nem criar conta. O PDF volta selado, com auditoria e validade jurídica."
            : "You send it, AI places the signatures and your client signs on their phone with no app and no account. The PDF comes back sealed, with an audit trail and legal validity.")}
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <a href={ctaHref} data-testid="cta-hero">
            <Button size="lg" className="h-12 w-full rounded-md bg-[var(--fs-moss)] px-6 text-base text-white hover:bg-[var(--fs-forest)] sm:w-auto">{copy("hero.cta", ctaLabel)}</Button>
          </a>
          <Button size="lg" variant="outline" onClick={scrollToFilm} data-testid="cta-demo" className="h-12 w-full rounded-md border-gray-300 px-6 text-base text-gray-800 sm:w-auto">
            <Play className="mr-2 h-4 w-4" />{copy("hero.cta2", pt ? "Ver como funciona (25 s)" : "See how it works (25 s)")}
          </Button>
        </div>
        {signup && <p className="mt-3 text-sm text-gray-500">{copy("hero.free", pt ? "Grátis para começar. Sem cartão de crédito." : "Free to start. No credit card.")}</p>}
        <ul className="mt-8 grid gap-2 text-sm text-gray-700 sm:grid-cols-2">
          {badges.map(([I, label]) => <li key={label} className="flex items-start gap-2"><I className="mt-0.5 h-4 w-4 shrink-0 text-[var(--fs-sage)]" />{label}</li>)}
        </ul>
      </div>
      <div className="min-w-0"><SignatureFlowMock pt={pt} /></div>
    </section>
  );
}
